/** Launch & mapping → Launch policy (plan §2 S1): the tour's own policy (a picker of the
 *  operator's, its franchise parent's and the global policies — what the PATCH accepts),
 *  the operator default beside it, whether the effective policy is enforced at all — its
 *  OWNER's `enable_launch_policies` (the operator's, its franchise parent's, or the
 *  platform's for a global policy: what `Launch_Policy.check_flag` reads) — and the
 *  effective flags: the nine channels, then the three "to any address / number" flags the
 *  soft-launch policies use as test redirects.
 *
 *  The grid follows the draft: pick another policy in edit mode and it shows that
 *  policy's flags (read from the picker list), marked "after saving"; clear it and it
 *  shows the operator default's. While the policy is not enforced the grid is muted —
 *  nothing is held back. Band node 3, `tour#policy`. */
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Typography } from '@mui/material';

import { LAUNCH_POLICY_FLAGS, type LaunchPolicyFlagKey, type LaunchPolicyFlags } from '@/api/launchPolicies';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useCellValue } from '@/features/records/fields/DraftCell';

import { launchPolicyActions } from '../../tourLinks';
import { TOUR_ENTITY } from '../catalog';
import { useLaunchPolicies } from '../queries';
import { useTourRecord } from '../TourRecordContext';

import { BlockAnchor } from './BlockAnchor';
import { TourFieldGrid } from './TourSectionPanel';

/** The three flags that let a notification reach a fixed address or number — what a
 *  soft launch uses to test without reaching guests. */
const REDIRECTS: ReadonlySet<LaunchPolicyFlagKey> = new Set([
  'outgoing_emails_to_address',
  'outgoing_sms_to_number',
  'outgoing_whatsapp_to_number',
]);
export const CHANNEL_FLAGS = LAUNCH_POLICY_FLAGS.filter((f) => !REDIRECTS.has(f.key));
export const REDIRECT_FLAGS = LAUNCH_POLICY_FLAGS.filter((f) => REDIRECTS.has(f.key));

export interface EffectivePolicy {
  /** The policy the tour launches with (own, else the operator default). */
  id: number | null;
  name: string | null;
  /** "own" or "operator default". */
  source: 'own' | 'default' | null;
  flags: LaunchPolicyFlags | null;
  /** Its owner enforces launch policies; null = not known (yet), or no policy. */
  enforced: boolean | null;
  /** The draft changed the policy: these are the flags after saving. */
  pending: boolean;
  /** The policy list a pick reads its flags from could not be loaded. */
  listFailed: boolean;
  retryList: () => void;
}

/** The effective policy of the draft's `launch_policy` value. */
export function useEffectivePolicy(): EffectivePolicy {
  const { detail } = useTourRecord();
  const { value, changed } = useCellValue(`${TOUR_ENTITY}:${detail.id}`, 'launch_policy');
  const policies = useLaunchPolicies(changed ? detail.tenant?.id : null);
  const list = { listFailed: changed && policies.isError, retryList: () => void policies.refetch() };
  const def = detail.operator_default_launch_policy;
  const own = typeof value === 'number' ? value : null;
  if (!changed) {
    const eff = detail.launch_policy ?? (def ? { id: def.id, name: def.name } : null);
    return {
      id: eff?.id ?? null,
      name: eff?.name ?? null,
      source: detail.launch_policy ? 'own' : def ? 'default' : null,
      flags: detail.launch_policy_flags,
      enforced: detail.launch_policy_enforced,
      pending: false,
      ...list,
    };
  }
  if (own === null) {
    return {
      id: def?.id ?? null,
      name: def?.name ?? null,
      source: def ? 'default' : null,
      flags: def?.flags ?? null,
      enforced: def ? def.enforced : null,
      pending: true,
      ...list,
    };
  }
  const picked = policies.data?.find((p) => p.id === own);
  return {
    id: own,
    name: picked?.name ?? null,
    source: 'own',
    flags: picked?.flags ?? null,
    enforced: picked?.enforced ?? null,
    pending: true,
    ...list,
  };
}

function FlagGroup({
  title,
  flags,
  keys,
  muted,
}: {
  title: string;
  flags: LaunchPolicyFlags | null;
  keys: typeof LAUNCH_POLICY_FLAGS;
  muted: boolean;
}) {
  return (
    <Box sx={{ display: 'grid', gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Box
        component="ul"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 0.25 }}
      >
        {keys.map((f) => {
          const on = flags?.[f.key];
          return (
            <Box
              component="li"
              key={f.key}
              data-flag={f.key}
              data-on={on === undefined ? undefined : String(on)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: muted || !on ? 'text.secondary' : 'text.primary' }}
            >
              {on === undefined ? (
                <Box component="span" sx={{ width: 16 }} />
              ) : on ? (
                <CheckIcon aria-hidden sx={{ fontSize: 16, color: muted ? 'text.secondary' : 'primary.main' }} />
              ) : (
                <CloseIcon aria-hidden sx={{ fontSize: 16 }} />
              )}
              <Typography variant="body2" component="span" sx={{ color: 'inherit' }}>
                {f.label}
                <Box component="span" sx={visuallyHidden}>
                  {on === undefined ? '' : on ? ': on' : ': off'}
                </Box>
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function PolicyBlock() {
  const { detail, links } = useTourRecord();
  const eff = useEffectivePolicy();
  const def = detail.operator_default_launch_policy;
  const enforced = eff.enforced;
  const enforcement =
    eff.id === null
      ? 'No launch policy — nothing is held back'
      : enforced === true
        ? 'Enforced'
        : enforced === false
          ? 'Not enforced — the policy’s tenant does not enforce launch policies, so nothing is held back'
          : eff.listFailed
            ? 'Couldn’t load the launch policies — enforcement and flags of this pick are unknown'
            : 'Whether it is enforced shows when the policy list loads';
  return (
    <BlockAnchor id="policy" label="Launch policy">
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
          Launch policy
        </Typography>
        <TourFieldGrid keys={['launch_policy']} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <Typography variant="body2" color="text.secondary" data-operator-default>
            Operator default:{' '}
            {def ? (
              <ValueActions actions={launchPolicyActions({ id: def.id, name: def.name }, links)}>
                <Box component="span" sx={{ color: 'text.primary' }}>
                  {def.name ?? `#${def.id}`}
                </Box>
              </ValueActions>
            ) : (
              'none set'
            )}
          </Typography>
          <Typography
            variant="body2"
            color={enforced === false || eff.id === null ? 'warning.dark' : 'text.secondary'}
            data-policies-enforced={String(enforced)}
          >
            {enforcement}
            {eff.pending && eff.id !== null && enforced !== null ? ' (after saving)' : ''}
          </Typography>
          {eff.listFailed && (
            <Button size="small" onClick={eff.retryList} data-load-error="launch-policies" sx={{ py: 0, minHeight: 24 }}>
              Retry
            </Button>
          )}
        </Box>
        {/* Not enforced: the flags' icons and words go muted (secondary text keeps
            4.5:1) and the line above says so — never an opacity over the block. */}
        <Box sx={{ display: 'grid', gap: 0.75 }} data-effective-flags data-muted={enforced === false || undefined}>
          <Typography variant="caption" color="text.secondary">
            Effective: <b>{eff.name ?? (eff.id ? `#${eff.id}` : 'no policy')}</b>
            {eff.source === 'own' ? ' · the tour’s own' : eff.source === 'default' ? ' · the operator default' : ''}
            {eff.pending ? ' · after saving' : ''}
            {eff.pending && !eff.flags && !eff.listFailed ? ' · flags load with the policy list' : ''}
          </Typography>
          <FlagGroup title="Channels" flags={eff.flags} keys={CHANNEL_FLAGS} muted={enforced === false} />
          <FlagGroup title="Test redirects — to any address or number" flags={eff.flags} keys={REDIRECT_FLAGS} muted={enforced === false} />
        </Box>
      </Box>
    </BlockAnchor>
  );
}

// px strings: in sx a bare `1` is 100%.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
